import { SphericalImageProps } from '@fabric-web/3d-viewer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as state from '@/components/Analysis/state';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { useAssemblySeenByViewpoints } from './useAssemblySeenByViewpoints';
import { useAtomValue, useSetAtom } from 'jotai';

export const useFocusOnSelectedAssembly = (selectedSpherical: SphericalImageProps | undefined) => {
  // Removing Temporarily until work for automatically focusing is done
  // const pickingMapsUpdateSequence = useAtomValue(state.pickingMapsUpdateSequence);
  const callbacks = useAtomValue(state.globalCallbacks);
  const lastDeeplinkUrlParameters = useAtomValue(state.lastDeeplinkUrlParameters);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const setCameraTarget = useSetAtom(state.cameraTarget);
  const structureRelationships = useAtomValue(state.structureRelationship);
  const structureLocations = useAtomValue(state.structureLocations);

  const assemblySeenByViewpoints = useAssemblySeenByViewpoints(selectedAssemblyId);

  const focalPoint = useMemo(() => {
    if (!selectedSpherical) return undefined;
    if (!selectedAssemblyId) return undefined;
    if (!assemblySeenByViewpoints) return undefined;
    const viewpoint = assemblySeenByViewpoints.find(
      ({ location }) => location.id === selectedSpherical?.id
    );
    return viewpoint?.focalPoint;
  }, [selectedSpherical, assemblySeenByViewpoints, selectedAssemblyId]);

  const annotation3dArray = useMemo(() => {
    if (!selectedAssemblyId) return undefined;
    if (!selectedSpherical) return undefined;
    if (!structureRelationships?.assemblyIdAndLocationIdToAnnotation3dArray) return undefined;

    const locationId = structureLocations?.find(
      (location) => location.name === selectedSpherical.name
    )?.id;
    if (!locationId) return undefined;

    const assemblyEntry =
      structureRelationships?.assemblyIdAndLocationIdToAnnotation3dArray[selectedAssemblyId];

    return assemblyEntry && Object.hasOwn(assemblyEntry, locationId)
      ? assemblyEntry[locationId]
      : [];
  }, [selectedAssemblyId, selectedSpherical, structureRelationships, structureLocations]);

  const focusOnAssembly = useCallback(() => {
    if (!selectedSpherical) return;
    if (!selectedAssemblyId) return;

    const { position } = selectedSpherical;

    if (focalPoint) {
      const { x, y, z } = focalPoint;
      setCameraTarget({
        position,
        lookAt: [x, y, z],
        transitionTime: 0.8,
      });
    } else if (annotation3dArray && callbacks?.findFocusPoint) {
      const { findFocusPoint } = callbacks;
      const focusPoint = findFocusPoint(position, 'annotation', annotation3dArray);
      if (focusPoint) {
        setCameraTarget({
          position,
          lookAt: focusPoint,
          transitionTime: 0.8,
        });
      }
    }
  }, [
    focalPoint,
    selectedAssemblyId,
    annotation3dArray,
    selectedSpherical,
    callbacks,
    setCameraTarget,
  ]);

  const prevSelectedSpherical = useRef<SphericalImageProps | undefined>(selectedSpherical);

  useEffect(() => {
    if (!lastDeeplinkUrlParameters) return;
    if (!selectedSpherical) {
      prevSelectedSpherical.current = undefined;
      return;
    }
    if (!focalPoint) return;

    if (prevSelectedSpherical.current === selectedSpherical) return;
    prevSelectedSpherical.current = selectedSpherical;

    if (selectedPoiId) return;
    if (
      lastDeeplinkUrlParameters.selectedSpherical === selectedSpherical &&
      lastDeeplinkUrlParameters.lookAt &&
      performance.now() < lastDeeplinkUrlParameters.time + 5000
    ) {
      return;
    }

    const { position } = selectedSpherical;
    const { x, y, z } = focalPoint;
    setCameraTarget({
      position,
      lookAt: [x, y, z],
      transitionTime: 0.8,
    });
  }, [focalPoint, selectedSpherical, selectedPoiId, setCameraTarget, lastDeeplinkUrlParameters]);

  return { focusOnAssembly };
};
